import styled from 'styled-components'
import PhoneInput from 'react-phone-input-2'
import OtpInput from 'react-otp-input'
import 'react-phone-input-2/lib/style.css'

// CAMC: Clubbed Auth Main Component
//
// Auth modal content is laid out as a single centered column. The container
// caps at ~420px so the form stays comfortable to read and tap on desktop,
// and fills the dialog on phones — no more awkward left/right gutter caused
// by a 600px shell with 315px children.

export const CAMCParent = styled.div`
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  font-family: 'Outfit', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  box-sizing: border-box;
`

export const CAMCHeading = styled.div`
  font-size: ${(props) => (props.secondary ? '22px' : '26px')};
  font-weight: 700;
  width: 100%;
  text-align: center;
  color: #0f172a;
  letter-spacing: -0.01em;
  font-family: 'Outfit', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  box-sizing: border-box;
  @media (min-width: 768px) {
    font-size: ${(props) => (props.secondary ? '24px' : '28px')};
  }
`

export const CAMCMaxWithRelativeContainer = styled.div`
  width: 100%;
  position: relative;
  box-sizing: border-box;
`

export const CAMCMethodButtonContainer = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'loading',
})`
  /* Standardised method button — consistent height / radius / typography for
     every auth option (Continue with Google, SMS, WhatsApp, Email, Mobile).
     Uses a softer rounded rectangle instead of the previous full pill. */
  width: 100%;
  min-height: 48px;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.2;
  border-radius: 12px;
  text-align: center;
  text-decoration: none;
  touch-action: manipulation;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  color: #1f2937;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  padding: 10px 20px 10px 52px;
  position: relative;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
  font-family: inherit;
  cursor: pointer;
  box-sizing: border-box;
  &:hover:not(:disabled) {
    border-color: #cbd5e1;
    background-color: #f8fafc;
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.06);
  }
  &:focus-visible {
    outline: 2px solid #7c3aed;
    outline-offset: 2px;
    border-color: #7c3aed;
  }
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`

export const CAMCMethodButtonImage = styled.img`
  position: absolute;
  left: 18px;
  width: 20px;
  height: 20px;
  object-fit: contain;
  display: block;
`

export const CAMCGeneralButton = styled(CAMCMethodButtonContainer).withConfig({
  shouldForwardProp: (prop) => prop !== 'loading' && prop !== 'success',
})`
  /* Primary / secondary generic button. Same footprint as a method button
     but without the left icon slot — text is centred. */
  padding: 10px 20px;
  min-height: 48px;
  ${(props) => props?.success && !props?.loading && 'background: #7c3aed; border-color: #7c3aed;'};
  ${(props) => props?.success && !props?.loading && 'color: #fff'};
  ${(props) => props?.success && props?.loading && `
    background: #7c3aed !important;
    color: #fff !important;
    border-color: #7c3aed !important;
  `};
  ${(props) => props?.success && `
    &:hover:not(:disabled) {
      background: #6d28d9;
      border-color: #6d28d9;
      box-shadow: 0 4px 12px rgba(124, 58, 237, 0.25);
    }
    &:active:not(:disabled) {
      background: #5b21b6;
      box-shadow: 0 2px 6px rgba(124, 58, 237, 0.2);
    }
  `};

  &:disabled {
    cursor: not-allowed !important;
    pointer-events: none;

    ${(props) => props?.success && props?.loading && `
      background: #7c3aed !important;
      color: #fff !important;
      border-color: #7c3aed !important;
      opacity: 0.85 !important;
    `};

    ${(props) => props?.success && !props?.loading && `
      background: #e2e8f0 !important;
      color: #94a3b8 !important;
      border-color: #e2e8f0 !important;
      opacity: 1 !important;
    `};

    ${(props) => !props?.success && `
      background: #f1f5f9 !important;
      color: #94a3b8 !important;
      border-color: #e2e8f0 !important;
      opacity: 1 !important;
    `};
  }
`

export const CAMCSeparator = styled.div`
  background: #e2e8f0;
  width: 100%;
  height: 1px;
`

export const CAMCGeneralInputLabel = styled.label`
  font-weight: 600;
  font-size: 14px;
  color: #334155;
  font-family: inherit;
  display: block;
  margin-bottom: 6px;
`

export const CAMCGeneralInput = styled.input`
  width: 100%;
  max-width: 100%;
  height: 52px;
  border-radius: 12px;
  display: flex;
  font-weight: 500;
  align-items: center;
  color: #0f172a;
  border: 1px solid #e2e8f0;
  padding: 0 42px 0 16px;
  position: relative;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  font-family: inherit;
  box-sizing: border-box;
  font-size: 16px; /* Prevents zoom on iOS */
  background: #ffffff;
  &:hover:not(:focus):not(:disabled) {
    border-color: #cbd5e1;
  }
  &:focus {
    outline: none;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.18);
  }
  &::placeholder {
    color: #94a3b8;
  }
`

export const CAMCErrorText = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  padding: 10px 12px;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.4;
  box-sizing: border-box;
`

export const CAMCPhoneInput = styled(PhoneInput)`
  width: 100%;
  position: relative;
  box-sizing: border-box;

  /* Completely remove CSS sprite flags - replaced with high-quality images */
  .flag {
    background-image: none !important;
    background-position: unset !important;
    background-size: unset !important;
  }

  .form-control {
    width: 100%;
    height: 52px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    color: #0f172a;
    background: #ffffff;
    font-size: 16px;
    font-weight: 500;
    padding-left: 64px;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    font-family: inherit;

    &:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.18);
      border-color: #7c3aed;
    }

    &:hover:not(:focus):not(:disabled) {
      border-color: #cbd5e1;
    }
  }

  .flag-dropdown {
    border: none;
    background: transparent;
    border-right: 1px solid #e2e8f0;
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    border-radius: 12px 0 0 12px;
    z-index: 1;

    &:hover,
    &:focus {
      background: transparent;
    }
  }
  
  .selected-flag {
    padding: 0 0 0 8px;
    width: 48px;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent !important;
    position: relative;
    overflow: hidden;
    
    &:hover,
    &:focus {
      background: transparent !important;
    }
  }
  
  /* Hide duplicate flags - show only the first visible flag */
  .selected-flag .flag {
    position: absolute;
    left: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px !important;
    height: 15px !important;
    visibility: hidden;
    opacity: 0;
    z-index: -1;
  }
  
  .selected-flag .flag:first-of-type,
  .selected-flag .flag:first-child,
  .selected-flag .flag:not([style*="display: none"]) {
    visibility: visible !important;
    opacity: 1 !important;
    position: relative !important;
    left: auto !important;
    top: auto !important;
    transform: none !important;
    z-index: 2 !important;
    display: inline-block !important;
    margin: 0 !important;
    vertical-align: middle !important;
    flex-shrink: 0;
    /* Completely remove CSS sprite - replaced by img elements via JS */
    background-image: none !important;
    background-position: unset !important;
    background-size: unset !important;
  }
  
  /* Ensure flag images load properly */
  .selected-flag .flag img.flag-img {
    width: 20px;
    height: 15px;
    object-fit: contain;
    display: inline-block;
    vertical-align: middle;
  }
  
  /* Hide duplicate arrows - show only the last one */
  .selected-flag .arrow,
  .selected-flag [class*="arrow"] {
    display: none !important;
  }
  
  .selected-flag .arrow:last-of-type,
  .selected-flag .arrow:last-child,
  .selected-flag [class*="arrow"]:last-of-type {
    display: inline-block !important;
    margin-left: 4px !important;
    vertical-align: middle !important;
    flex-shrink: 0;
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 4px solid #242424;
    position: relative;
    z-index: 2;
  }
  
  .country-list {
    background: #ffffff !important;
    border: 1px solid #707070 !important;
    border-radius: 5px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
    max-height: 200px !important;
    width: 300px !important;
    z-index: 99999 !important;
    position: fixed !important;
    overflow-y: auto !important;
    display: block !important;
  }
  
  .country-list .country {
    display: flex !important;
    align-items: center !important;
    padding: 8px 10px !important;
    color: #242424 !important;
  }
  
  .country-list .flag {
    display: inline-block !important;
    width: 20px !important;
    height: 15px !important;
    margin-right: 8px !important;
    /* Remove CSS sprite */
    background-image: none !important;
    background-position: unset !important;
    background-size: unset !important;
  }
  
  .country-list .flag img.flag-img {
    width: 20px;
    height: 15px;
    object-fit: contain;
    display: inline-block;
    vertical-align: middle;
    margin-right: 8px;
  }
  
  .country {
    display: flex;
    padding: 8px 10px;
    color: #242424;
    
    &:hover {
      background: #f5f5f5;
    }
  }
  
  .country-name {
    color: #242424;
    flex: 1;
    margin-right: 8px;
  }
  
  .dial-code {
    color: #707070;
    font-size: 14px;
  }
  
  .search {
    background: #ffffff;
    padding: 10px;
    position: sticky;
    top: 0;
    z-index: 1;
  }
  
  .search-box {
    width: 100%;
    padding: 6px 10px;
    border: 1px solid #d9d9d9;
    border-radius: 3px;
    font-size: 14px;
    color: #242424;
    
    &:focus {
      outline: none;
      border-color: #514387;
    }
  }
  
  .special-label {
    display: none;
  }
  
  @media (max-width: 767px) {
    max-width: 100%;
    
    .form-control {
      font-size: 16px; /* Prevents zoom on iOS */
    }
  }
`

export const CAMCCaptcha = styled.div`
  /* Invisible reCAPTCHA container - positioned absolutely to not affect layout */
  position: absolute;
  visibility: hidden;
  height: 0;
  overflow: hidden;
`

export const CAMCOtpInput = styled(OtpInput)`
  width: 100%;
  max-width: 315px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  
  input {
    width: 100% !important;
    height: 48px;
    border: 1px solid #707070;
    border-radius: 8px;
    background: #ffffff;
    font-size: 20px;
    font-weight: 600;
    color: #242424;
    text-align: center;
    transition: all 0.3s ease-in-out;
    font-family: 'Outfit', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    
    &:focus {
      outline: none;
      border-color: #514387;
      box-shadow: 0 0 0 2px rgba(81, 67, 135, 0.1);
    }
    
    &:hover {
      border-color: #000;
    }
  }
  
  @media (max-width: 767px) {
    gap: 6px;
    
    input {
      height: 44px;
      font-size: 18px;
    }
  }
`

export const CAMCResendOtpContainer = styled.div`
  width: 100%;
  max-width: 315px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
  font-size: 14px;
  color: #707070;
  font-family: 'Outfit', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  box-sizing: border-box;
  @media (max-width: 767px) {
    max-width: 100%;
    padding: 0 5px;
    font-size: 13px;
  }
  
  span {
    color: #514387;
    font-weight: 600;
    cursor: pointer;
    
    &:hover {
      text-decoration: underline;
    }
    
    &[data-disabled="true"] {
      color: #707070;
      cursor: default;
      text-decoration: none;
    }
  }
`
